<script>
    /* global  PP */

    import { onMount } from "svelte";
    import Button from "../Button";

    /**
     * Unless you have arranged for a special case with the product team, use
     * the value
     * `https://www.propublica.org/api_components/modal-default.json`
     * @type {string}
     */
    export let contentUrl;

    const defaultData = {
        heading: "Journalism That Holds Power to Account",
        deck: "Sign up for ProPublica’s Big Story newsletter and get our latest stories delivered straight to your inbox.",
        ctaType: "newsletter",
        newsletterList: "The Big Story",
    };

    /**
     * Variables to hold open/close states
     */
    let contentData = {};

    let showCta = false;
    let showToast = false;
    let toastText = "";
    let toastColor = "var(--blue-70)";

    export let debugForcePanelToShow = false;

    /**
     * Cookie functions
     */

    function shouldCtaDisplay() {
        if (!window.PP.funnel) {
            return true;
        }

        if (debugForcePanelToShow) {
            return true;
        }

        const frequency = 3;
        const signupStatus = window.PP.funnel.is_signedup(
            contentData.newsletterList || "The Big Story"
        );
        const sessionCount = window.PP.funnel.get_session_count();
        // false means user has recently "rejected" modal
        const displayStatus = window.PP.funnel.current_state.displayModal;

        return (
            !signupStatus &&
            displayStatus &&
            (sessionCount === 1 || sessionCount % frequency === 0)
        );
    }

    function closeToast() {
        showToast = false;
    }

    function closeModal() {
        showCta = false;
        closeToast();
    }

    /**
     * Modal interaction functions
     */
    function rejectCta() {
        closeModal();

        // TODO: see if you can do this in GTM now
        if (window.dataLayer) {
            window.dataLayer.push({
                event: "modal_close",
            });
        }

        PP.funnel.deactivate_modal();
        PP.funnel.save_cookie();
    }

    /**
     *
     * @param {string} message
     * @param { {style?: string, closingTime?: number} } opt
     */
    function openToast(message, opt) {
        const options = {
            style: opt.style || "default",
            closingTime: opt.closingTime || 3000,
        };

        toastText = message;
        switch (options.style) {
            case "success":
                toastColor = "var(--green-70)";
                break;
            case "failure":
                toastColor = "var(--red-70)";
                break;
            default:
                toastColor = "var(--blue-70)";
        }
        showToast = true;

        setTimeout(() => {
            showToast = false;
        }, options.closingTime);
    }

    /**
     * @param {SubmitEvent} event
     */
    function newsletterSubmit(event) {
        const elCtaFormButton = document.querySelector("#cta-form-button");
        const form = event.target;
        const url = form.getAttribute("action");
        const data = new FormData(form);
        elCtaFormButton.setAttribute("disabled", true);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        xhr.responseType = "json";
        xhr.setRequestHeader(
            "Content-type",
            "application/x-www-form-urlencoded"
        );

        xhr.onload = () => {
            if (xhr.status !== 200) {
                // non-HTTP error
                openToast(
                    "There was a problem with your newsletter signup, please try again.",
                    { style: "failure", closingTime: 5000 }
                );
            } else {
                closeModal();

                openToast("Thank you for subscribing!", {
                    style: "success",
                    closingTime: 5000,
                });

                if (window.PARSELY && window.PARSELY.conversions) {
                    window.PARSELY.conversions.trackNewsletterSignup("Modal");
                }

                PP.funnel.add_newsletter_signup(contentData.newsletterList);
                PP.funnel.deactivate_modal();
                PP.funnel.save_cookie();

                // if (debug) {
                //     console.log("current_state:");
                //     console.log(PP.funnel.current_state);
                //     console.log("Signed up for:");
                //     console.log(PP.funnel.get_newsletter_signups());
                // }
            }
        };
        xhr.onerror = () => {
            // non-HTTP error
            form.classList.remove("submitting");
            form.classList.add("error");

            elCtaFormButton.value = "Check email address and try again";
            elCtaFormButton.setAttribute("disabled", false);
        };
        xhr.send(new URLSearchParams(data).toString());
    }

    function openModal() {
        if (Object.keys(contentData).length) {
            showCta = true;
            window.PP.utils.cookie("pp_cta_last_viewed", Date.now());
        } else {
            console.error("CTA: contentData is empty", contentData);
        }
    }

    /**
     * Loads user funnel cookie and tries to save a session to the cookie.
     *
     * Loading the cookie is a prereq for shouldCtaDisplay() to run correctly,
     * as that function expects to know the # of recent sessions + whether the
     * user has previously dismissed the modal (or signed up for a newsletter).
     *
     * Saving a session is necessary so that recent session count is correctly
     * tracked; this affects *future* occurrences of this component mounting,
     * which check the session count.
     *
     * On www, this function is redundant because a different version of it
     * already runs at
     * https://github.com/propublica/propublica.org-craftv3/blob/36b396d6bcef80aa18ef796efe3f2287792f11de/assets/js/user-funnel-init.js
     * However, running these functions twice on one pageview should not do
     * harm.
     */
    function userFunnelInit() {
        // idempotent
        window.PP.funnel.load_cookie();
        // Does nothing if another session was saved in the last 30 minutes:
        // https://github.com/propublica/propublica.org-craftv3/blob/36b396d6bcef80aa18ef796efe3f2287792f11de/assets/js/user-funnel.js#L408
        // Therefore, no big deal if function runs twice on a single pageload.
        //
        // The one corner case is if the page was loaded ~11:59:59pm and the
        // function runs once before + once after day-boundary, in which case
        // it'll count 2 sessions. This is fine, sufficiently aligned with our
        // intended definition of a session.
        window.PP.funnel.try_add_session();
        // idempotent
        window.PP.funnel.save_cookie();
    }

    /**
     * Handles initial load
     */
    function loadModal() {
        userFunnelInit();
        if (shouldCtaDisplay()) {
            fetch(contentUrl)
                .then((res) => {
                    if (!res.ok)
                        throw new Error(
                            `Error loading contentUrl (status ${res.status})`
                        );
                    return res.json();
                })
                .then((data) => {
                    contentData = data;
                    if (
                        !contentData.heading ||
                        !contentData.deck ||
                        !contentData.ctaType
                    )
                        throw new Error(
                            `Content parsed but was empty or incomplete.`
                        );
                })
                .catch((err) => {
                    console.log(
                        "Could not load CTA content from URL, using default data. See next line for error."
                    );
                    console.error(err);
                    contentData = defaultData;
                })
                .finally(() => {
                    // shouldCtaDisplay() can change from start of func to now
                    if (shouldCtaDisplay()) openModal();
                });
        }
    }

    onMount(() => {
        // If page is already fully loaded
        if (document.readyState === "complete") {
            loadModal();
            return undefined;
        }

        // Otherwise, wait until page is fully loaded ()
        window.addEventListener("load", loadModal);
        // When component unmounted, remove event listener (code not run 99% of
        // the time, but good practice)
        return () => {
            window.removeEventListener("load", loadModal);
        };
    });
</script>

<!--
You must load the following script tags (as "classic" JS scripts, not modules
😭) separately on the page, in order for this component to work:

- https://assets.propublica.org/static/prod/v5/js/deploy/utils.js
- https://assets.propublica.org/static/prod/v5/js/deploy/user-funnel.js
- https://assets.propublica.org/static/prod/v5/js/deploy/recaptcha-init.js
- (recaptcha-init.js must be fully loaded before this one: beware of async/defer + ordering 😔)
  https://www.google.com/recaptcha/api.js?onload=grecaptchaLoaded&render=6LdI1rAUAAAAACI0GsFv-yRpC0tPF5ECiIMDUz2x

This is because BottomPanelCta.svelte relies on some globals set in those
scripts.

It's fine if these scripts are compiled and run as part of a bigger script (this
is what propublica/propublica.org-craftv3 does), or loaded with async or defer,
as long as they are executed in a non-module context.

They should not be loaded dynamically using script tags inserted into the DOM at
runtime  (i.e. do not use RecaptchaInit.svelte from this repo together with
this component).

Note: If you are using this in an app based on propublica/baker-template or
propublica/newsapps-rails-template, you probably don't have to worry about any
of this since it has been taken care of for you by default.

@component
-->

<div
    data-pp-location="modal"
    data-pp-view
    data-pp-category="newsletter_signup"
    id="cta"
    class="cta cta-type-panel cta-fixed {showCta || debugForcePanelToShow
        ? 'cta-visible'
        : 'cta-closed'}"
    aria-hidden="true"
>
    <button id="cta-close" class="cta-close" on:click={rejectCta}>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    </button>
    <div class="cta-container cta-{contentData.ctaType}">
        <div class="cta-message">
            <h2 class="cta-heading">{contentData.heading}</h2>
            <p class="cta-text">
                {contentData.deck}
            </p>
        </div>
        {#if contentData.ctaType === "newsletter"}
            <div class="cta-please">Please enter your email address:</div>
            <form
                id="cta-form"
                class="cta-form"
                method="post"
                action="https://signup.propublica.org/newsletter/turing"
                data-pp-action="subscribe"
                on:submit|preventDefault={newsletterSubmit}
            >
                <!--
                TODO: Replace onfocus with a Svelte-native event handler, or at
                    least changeto use vanilla JS `addEventListener`. As
                    written, will break in Svelte 5:
                    https://svelte-5-preview.vercel.app/docs/event-handlers
                -->
                <input
                    type="email"
                    name="email"
                    id="cta-email"
                    class="cta-email"
                    placeholder="you@example.com"
                    required
                    onfocus="recaptchaInit()"
                />
                <input
                    type="hidden"
                    name="stlist"
                    value={contentData.newsletterList}
                />
                <input type="hidden" name="placement" value="modal" />
                <input
                    type="hidden"
                    name="success_location"
                    value="https://www.propublica.org/forms/thankyou"
                />
                <input
                    type="hidden"
                    name="error_location"
                    value="https://www.propublica.org/forms/error"
                />
                <input
                    type="hidden"
                    name="source"
                    value="https://www.propublica.org/"
                />
                <input type="hidden" name="ajax" value="true" />
                <Button
                    bgColor="var(--color-accent-90)"
                    textColor="var(--color-bg)"
                    buttonSize="var(--scale-4)"
                    miscAttrs={{
                        id: "cta-form-button",
                        type: "submit",
                    }}
                >
                    <span class="cta-long">Get the Newsletter</span>
                    <span class="cta-short">Subscribe</span>
                </Button>
            </form>
        {:else if contentData.ctaType === "donation"}
            <Button
                elementType="a"
                href={contentData.donateUrl}
                bgColor="var(--color-accent-90)"
                textColor="var(--color-bg)"
                buttonSize="var(--scale-4)"
                miscAttrs={{
                    "data-pp-view": "",
                    id: "cta-donate",
                }}
            >
                Donate Now
            </Button>
        {/if}
    </div>
</div>
<div
    id="cta-toast"
    class="cta-toast {showToast ? 'cta-toast-open' : ''}"
    style:background-color={toastColor}
    role="alert"
>
    <div id="cta-toast-text" class="cta-toast-text">{toastText}</div>
    <button
        id="cta-toast-button"
        class="cta-toast-button"
        on:click={closeToast}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    </button>
</div>

<style>
    .cta {
        background: var(--color-bg);
        position: fixed;
        opacity: 0;
        z-index: 2200;
        transition: ease-out 0.2s transform, ease-out 0.1s opacity;
        transform: translateY(100%);
    }

    .cta-toast {
        background-color: var(--color-accent-70);
        color: var(--white);
        z-index: 3000;
        position: fixed;
        margin-left: auto;
        margin-right: auto;
        max-width: 20rem;
        bottom: var(--spacing1);
        left: 0;
        right: 0;
        padding: 0.8rem;
        font-family: var(--fonts-sans);
        font-size: var(--scale-1);
        border-radius: 2px;
        display: none;
        width: fit-content;
    }

    .cta-toast-open {
        display: flex;
    }

    .cta-toast-text {
        align-self: center;
        flex: 1;
        line-height: var(--line-height-1);
    }

    .cta-toast-button {
        background-color: transparent;
        color: var(--white);
        margin-right: calc(-1 * var(--spacing-1));
        flex-shrink: 0;
        margin: -0.2rem -0.8rem -0.2rem 0.4rem;
    }

    .cta-container :global(.button:hover) {
        background-color: var(--color-accent-50);
        opacity: 1;
    }

    .cta .cta-email {
        border: 1px solid var(--cool-30);
        background-color: var(--color-bg);
        font-size: var(--scale-1);
    }

    .cta-container :global(.button),
    .cta .cta-email {
        padding: 0.75em 0.5em;
        text-align: center;
        border-radius: 4px;
        font-size: var(--scale-1);
    }

    .cta-type-panel {
        justify-content: center;
        margin: 0 calc(-1 * (100vw - 100%) / 2);
        padding: 1.5rem 0;
        bottom: 0;
        left: 0;
        box-shadow: 0 0 140px rgb(0 0 0 / 10%);
    }

    .cta-type-panel .cta-heading {
        font-size: var(--scale2);
    }

    .cta-type-panel .cta-container {
        max-width: 1344px;
        margin-left: auto;
        margin-right: auto;
        padding: 0 2.985984rem;
        column-gap: var(--spacing3);
        align-items: center;
    }

    .cta-type-panel.cta-fixed {
        width: 100%;
    }

    .cta-message {
        grid-gap: var(--spacing-1);
        display: flex;
        flex-direction: column;
        align-self: center;
        flex: 1;
    }

    .cta-short {
        display: none;
    }

    .cta-fixed {
        position: fixed;
    }

    .cta-close {
        background-color: transparent;
        position: absolute;
        right: 0;
        top: 0;
        color: var(--color-text-meta);
    }

    .cta-close svg,
    .cta-toast-button svg {
        width: 24px !important;
        height: 24px !important;
    }

    .cta-container {
        display: flex;
        flex-direction: row;
    }

    .cta-donation {
        flex-direction: column;
        row-gap: var(--spacing0);
    }

    .cta-visible {
        opacity: 1 !important;
        transform: translateY(0);
    }

    .cta-flex {
        display: flex !important;
    }

    .cta-heading {
        line-height: var(--line-height-3);
        font-size: var(--scale3);
        font-family: var(--fonts-hed);
        font-weight: 700;
        text-align: center;
    }

    .cta-text {
        font-size: 16px;
        font-size: var(--scale-1);
        font-family: var(--fonts-sans);
        text-align: center;
        line-height: 1.5;
    }

    .cta-form {
        display: flex;
        flex: 1;
        flex-direction: column;
        grid-gap: 10px;
    }

    .cta-closed {
        display: none !important;
    }

    .cta-please {
        display: none;
        font-weight: 700;
    }

    .cta-expanded .cta-message {
        display: none;
    }

    .cta-expanded .cta-please {
        display: inline;
        font-size: 14px;
        font-family: var(--fonts-sans);
    }

    .cta-type-panel {
        background-color: var(--color-neutral-10);
    }

    @media (prefers-color-scheme: dark) {
        .cta-type-panel {
            background-color: var(--color-neutral-10);
        }
    }

    @media (max-width: 768px) {
        .cta-type-panel .cta-text {
            margin-bottom: var(--spacing-1);
        }

        .cta-container :global(.button),
        .cta-email {
            font-size: 16px !important;
        }
    }

    @media (max-width: 575px) {
        .cta-type-panel .cta-container {
            flex-direction: column;
        }
        .cta-text {
            font-size: 13px;
        }

        .cta-form {
            flex-direction: row;
            grid-gap: 0;
        }

        .cta-type-panel .cta-email {
            margin-bottom: 0;
            flex: 1;
        }

        .cta-type-panel .cta-short {
            display: inline;
        }

        .cta-type-panel .cta-long {
            display: none;
        }

        .cta-type-panel .cta-heading {
            max-width: 330px;
            margin-left: auto;
            margin-right: auto;
        }

        .cta-type-panel .cta-container {
            padding: 0 1.44rem;
            column-gap: var(--spacing0);
        }

        .cta-type-panel .cta-email {
            margin-bottom: 0;
            border-radius: 4px 0px 0px 4px;
            text-align: left;
            padding-left: 15px;
            padding-right: 15px;
        }

        .cta-type-panel .cta-container :global(.button) {
            margin-bottom: 0;
            border-radius: 0 4px 4px 0;
            padding-left: 15px;
            padding-right: 15px;
        }
    }
</style>
